import { Form, Head } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { BrandContextStrip } from "@/components/brand/brand-context-strip"
import InputError from "@/components/input-error"
import OnboardingPasswordRules from "@/components/onboarding/onboarding-password-rules"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import {
  type AnonymousPostHogBootstrap,
  capturePostHogEvent,
  initPostHogAnonymous,
} from "@/entrypoints/posthog-analytics"
import { useOnline } from "@/hooks/use-online"
import PublicLayout from "@/layouts/public-layout"
import { signUpPath } from "@/routes"

export interface MinimalSignupBrand {
  /** Brand whose invite the creator is acting on. Surfaced in the slim
   *  top-bar (logo + name) — Crew is NOT white-label, so brand is DATA. */
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface MinimalSignupCandidateBrand {
  brand_name: string
  display_name: string
  logo_url: string | null
}

export interface MinimalSignupPasswordRequirements {
  min_length: number
  rule_keys: string[]
}

export interface MinimalSignupInviter {
  /** OKS-458 — inviter display name for the strip's "Invited by {…}"
   *  line. Disclosure-safe: name + initials only, no email/id. */
  name: string
  initials: string
}

export interface MinimalSignupProps {
  /** Hidden form field — the invite token rides through to backend. */
  invite_token: string
  /** Brand chrome data. */
  brand: MinimalSignupBrand
  /** Candidate metadata — `display_name` pre-fills the full-name field. */
  candidate: MinimalSignupCandidateBrand
  /** OKS-458 — inviter `{name, initials}` for the sticky strip's second
   *  line, or null when there's no resolvable inviter. */
  inviter: MinimalSignupInviter | null
  /** Server-rendered password rules. */
  password_requirements: MinimalSignupPasswordRequirements
  /** Echoed values from BE on validation re-render (OKS-458 — name +
   *  email retained; password never echoed). */
  form_values: { user?: { name?: string; email?: string } }
  /** Server-resolved TOS / Privacy URLs (passive text-links — NOT
   *  acceptance checkboxes per UX §4 lock). */
  tos_url: string
  privacy_url: string
  /** OKS-534 — minimal ANON PostHog bootstrap (key+host) so the pre-auth
   *  `signup_started` event can fire; null when no public key is set.
   *  Distinct from the app-wide `posthog` shared prop by design. */
  posthog_anon: AnonymousPostHogBootstrap | null
}

/**
 * MinimalSignup — OKS-24 / OKS-458 §02 invite signup. THREE inputs:
 * full name, email, password. (OKS-458 dropped the password-confirmation
 * field per the locked design and added the full-name field; the
 * password-rules helper line is the typo safeguard.)
 *
 * **TOS / Privacy is a PASSIVE TEXT-LINK** below the CTA, NOT a checkbox
 * (UX Spec §4 lock + BE TRD §Key Decisions). Server auto-sets
 * `users.tos_accepted_at = Time.current` on successful create — no form
 * field, no permit, no validation error path. The Vitest negative canary
 * (below) asserts `name="acceptance[tos]"` is NEVER rendered.
 *
 * **Wire format** (OKS-458):
 *   - `name="user[name]"`
 *   - `name="user[email]"`
 *   - `name="user[password]"`
 *   - hidden `name="invite_token"` (top-level, NOT under `user[…]`)
 *   - NO `name="user[password_confirmation]"` (field removed)
 *
 * **Inertia `<Form>`** from `@inertiajs/react` — the render-prop
 * `errors` object surfaces flat dotted keys (`errors["user.name"]`,
 * `errors["user.email"]`, `errors["user.password"]`).
 *
 * **5-state matrix** (UX Spec §5):
 *   - Loading  — `processing` from render prop disables submit + spinner
 *   - Empty    — first visit; name pre-fills from `candidate.display_name`
 *   - Error    — per-field <InputError>; form-level base via <Alert>;
 *                name + email retained from `form_values` (PRD AC2)
 *   - Success  — server 303 → §02 arrival (`signup_welcome_path`)
 *   - Offline  — `useOnline=false` disables submit + helper hint
 */
export default function MinimalSignup({
  invite_token,
  brand,
  candidate,
  inviter,
  password_requirements,
  form_values,
  tos_url,
  privacy_url,
  posthog_anon,
}: MinimalSignupProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // OKS-534 — fire the one pre-auth client event on mount. Anon-inits
  // posthog-js from the page bootstrap (no-op without a key), then
  // captures `signup_started`. Fire-and-forget; invisible to the user.
  useEffect(() => {
    initPostHogAnonymous(posthog_anon)
    capturePostHogEvent("signup_started", { has_invite: true })
  }, [posthog_anon])

  const passwordRulesId = "signup-minimal-password-rules"

  return (
    <PublicLayout>
      <Head>
        <title>{t("signup_minimal.head_title")}</title>
      </Head>
      <div
        data-testid="signup-minimal"
        data-state="empty"
        className="bg-background flex min-h-svh flex-col"
      >
        {/*
          OKS-383 — sticky brand-context strip replaces the OKS-303 slim
          logo+name header (UX Spec §2.2). "Joining {Brand}'s crew" stays
          pinned to the top through scroll + field focus so the candidate
          never loses the brand anchor. `<BrandContextStrip>` owns the
          logo→monogram fallback and the `signup-minimal-brand-name`
          testid (kept stable for QA).
        */}
        <BrandContextStrip
          brandName={brand.name}
          logoUrl={brand.logo_url}
          inviterName={inviter?.name ?? null}
          testId="signup-minimal-context-strip"
        />

        <main
          role="main"
          tabIndex={-1}
          className="mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-6 py-8 outline-none"
        >
          <div className="flex flex-col gap-1">
            <h1
              ref={headingRef}
              tabIndex={-1}
              data-testid="signup-minimal-heading"
              className="text-2xl font-bold tracking-tight outline-none"
            >
              {t("signup_minimal.heading")}
            </h1>
            <p
              data-testid="signup-minimal-subtitle"
              className="text-muted-foreground text-sm"
            >
              {t("signup_minimal.subtitle")}
            </p>
          </div>

          <Form
            method="post"
            action={signUpPath()}
            className="flex flex-col gap-4"
            data-testid="signup-minimal-form"
          >
            {({ processing, errors }) => {
              const baseErrors = errors.base
              const nameErrors = errors["user.name"]
              const emailErrors = errors["user.email"]
              const passwordErrors = errors["user.password"]
              const submitDisabled = processing || !online
              return (
                <>
                  {/* Hidden invite token — top-level (NOT user[…]). */}
                  <input
                    type="hidden"
                    name="invite_token"
                    value={invite_token}
                    data-testid="signup-minimal-invite-token"
                  />

                  {Array.isArray(baseErrors) && baseErrors.length > 0 && (
                    <Alert
                      variant="destructive"
                      data-testid="signup-minimal-error-base"
                    >
                      <AlertDescription>
                        {baseErrors.join(", ")}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/*
                    OKS-458 — full-name field (first in reading order).
                    Pre-fills from the retained value on an error re-render,
                    else from `candidate.display_name`; stays editable.
                    `truncate`-friendly: long values ellipsize inside the
                    input bounds (no wrap) per UX Spec §5 edge state.
                  */}
                  <div className="grid gap-2">
                    <Label htmlFor="signup-minimal-name">
                      {t("signup_minimal.name_label")}
                      {/* OKS-728 — required marker; native `required` dropped
                          so blank submits reach the server for a translated
                          inline error (no native bubble). */}
                      <span
                        className="text-destructive ml-0.5"
                        aria-hidden="true"
                      >
                        *
                      </span>
                    </Label>
                    <Input
                      id="signup-minimal-name"
                      name="user[name]"
                      type="text"
                      autoComplete="name"
                      aria-required="true"
                      defaultValue={
                        form_values.user?.name ?? candidate.display_name ?? ""
                      }
                      placeholder={t("signup_minimal.name_placeholder")}
                      className="text-ellipsis"
                      aria-invalid={nameErrors ? "true" : undefined}
                      aria-describedby={
                        nameErrors ? "signup-minimal-error-name" : undefined
                      }
                      data-testid="signup-minimal-name"
                    />
                    <InputError
                      id="signup-minimal-error-name"
                      messages={nameErrors}
                      data-testid="signup-minimal-error-name"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="signup-minimal-email">
                      {t("signup_minimal.email_label")}
                      <span
                        className="text-destructive ml-0.5"
                        aria-hidden="true"
                      >
                        *
                      </span>
                    </Label>
                    <Input
                      id="signup-minimal-email"
                      name="user[email]"
                      type="email"
                      autoComplete="email"
                      aria-required="true"
                      defaultValue={form_values.user?.email ?? ""}
                      placeholder={t("signup_minimal.email_placeholder")}
                      className="text-ellipsis"
                      aria-invalid={emailErrors ? "true" : undefined}
                      aria-describedby={
                        emailErrors ? "signup-minimal-error-email" : undefined
                      }
                      data-testid="signup-minimal-email"
                    />
                    <InputError
                      id="signup-minimal-error-email"
                      messages={emailErrors}
                      data-testid="signup-minimal-error-email"
                    />
                  </div>

                  <OnboardingPasswordRules
                    id={passwordRulesId}
                    minLength={password_requirements.min_length}
                    ruleKeys={password_requirements.rule_keys}
                  />

                  <div className="grid gap-2">
                    <Label htmlFor="signup-minimal-password">
                      {t("signup_minimal.password_label")}
                      <span
                        className="text-destructive ml-0.5"
                        aria-hidden="true"
                      >
                        *
                      </span>
                    </Label>
                    <Input
                      id="signup-minimal-password"
                      name="user[password]"
                      type="password"
                      autoComplete="new-password"
                      aria-required="true"
                      placeholder={t("signup_minimal.password_placeholder")}
                      aria-invalid={passwordErrors ? "true" : undefined}
                      aria-describedby={[
                        passwordRulesId,
                        passwordErrors ? "signup-minimal-error-password" : null,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      data-testid="signup-minimal-password"
                    />
                    <InputError
                      id="signup-minimal-error-password"
                      messages={passwordErrors}
                      data-testid="signup-minimal-error-password"
                    />
                  </div>

                  {!online && (
                    <p
                      className="text-muted-foreground text-sm"
                      role="status"
                      aria-live="polite"
                      data-testid="signup-minimal-offline-helper"
                    >
                      {t("signup.errors.offline_helper")}
                    </p>
                  )}

                  <div className="bg-background sticky bottom-0 -mx-6 mt-2 flex flex-col-reverse gap-3 border-t px-6 py-4 sm:static sm:mx-0 sm:p-0">
                    <Button
                      type="submit"
                      disabled={submitDisabled}
                      aria-disabled={submitDisabled}
                      data-testid="signup-minimal-submit"
                      className="w-full"
                      size="lg"
                    >
                      {processing && <Spinner className="size-4" />}
                      {processing
                        ? t("signup_minimal.submit_processing")
                        : t("signup_minimal.submit")}
                    </Button>
                  </div>

                  {/*
                    Passive TOS / Privacy text-links below the CTA — NOT a
                    checkbox (UX Spec §4 lock; supersedes OKS-22). Server
                    auto-sets `users.tos_accepted_at` on successful create.
                    Vitest negative canary asserts no `name="acceptance[tos]"`
                    is ever rendered on this page.
                  */}
                  <p
                    data-testid="signup-minimal-tos-privacy"
                    className="text-muted-foreground text-center text-xs"
                  >
                    {t("signup_minimal.tos_privacy.prefix")}
                    <a
                      href={tos_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="signup-minimal-tos-link"
                      className="hover:text-foreground underline underline-offset-2"
                    >
                      {t("signup_minimal.tos_privacy.tos_label")}
                    </a>
                    {t("signup_minimal.tos_privacy.separator")}
                    <a
                      href={privacy_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="signup-minimal-privacy-link"
                      className="hover:text-foreground underline underline-offset-2"
                    >
                      {t("signup_minimal.tos_privacy.privacy_label")}
                    </a>
                    {t("signup_minimal.tos_privacy.suffix")}
                  </p>
                </>
              )
            }}
          </Form>
        </main>
      </div>
    </PublicLayout>
  )
}
